import {
  ApplicationStatus,
  ProfileType,
  useOwnerProfile,
  useProfile,
  useTheme,
} from "@up/data";
import {
  Animation,
  ApplicationWarning,
  LoadingError,
  LoadingModal,
  Toast,
} from "@up/ui";

import { useCart } from "../CartContext";
import { CART_STATUS } from "../constants";
import { CartEmpty } from "../CartEmpty";
import { Layout } from "../Layout";
import { Upsell } from "../Upsell";
import { PaymentStep } from "./PaymentStep";
import { ReviewStep } from "./ReviewStep";

type CheckoutProps = {
  partnerSiteBaseURL: string;
  stripePublicKey: string;
};

export default function CheckoutAuthenticated({
  partnerSiteBaseURL,
  stripePublicKey,
}: CheckoutProps) {
  const theme = useTheme();
  const cart = useCart();
  const profile = useProfile();
  const ownerProfile = useOwnerProfile();

  const showDOB = (() => {
    const profileDOB = profile?.data?.dob;
    if (profileDOB) return true;

    return (
      cart.data?.lineItems?.some(
        (item) => item.catalogueItem.type === "collegeCredit",
      ) ?? false
    );
  })();

  const showGuardianEmail =
    profile?.data?.type !== ProfileType.Guardian && !cart.data?.guardian?.email;

  if (cart.status === CART_STATUS.LOADING) {
    return (
      <Layout headingClass={theme.heading1Class}>
        <LoadingModal />
      </Layout>
    );
  }

  if (cart.status === CART_STATUS.ERROR) {
    return (
      <Layout headingClass={theme.heading1Class}>
        <LoadingError error="Sorry, we could not load your cart" />
      </Layout>
    );
  }

  if (cart.status === CART_STATUS.SUCCESS && cart.data) {
    const items = cart.data.lineItems?.length ? cart.data.lineItems : [];
    const isFree =
      (cart.data.isFree ?? false) || cart.data.summary.priceToPay === 0;
    const showApplicationWarning =
      ownerProfile?.application?.status !== ApplicationStatus.Approved &&
      ownerProfile?.application?.status !== ApplicationStatus.AdminCompleted;

    if (showApplicationWarning) {
      return (
        <Layout headingClass={theme.heading1Class}>
          <ApplicationWarning
            appStatus={ownerProfile?.application?.status}
            isStudent={profile?.data?.type === ProfileType.Student}
          />
        </Layout>
      );
    }

    if (!items.length) {
      return (
        <Layout headingClass={theme.heading1Class}>
          {cart.addItemError ? (
            <div className="mb-4">
              <Toast variant="error">{cart.addItemError}</Toast>
            </div>
          ) : null}
          <CartEmpty />
          <br />
          <div className="max-w-md mx-auto">
            <Upsell
              analytics={{
                location: "checkout",
                lineItemCount: String(cart.data.lineItems?.length ?? 0),
              }}
              showToggle={false}
              title={"Interested in adding a course?"}
            />
          </div>
          <br />
        </Layout>
      );
    }

    return (
      <Layout
        currentStep={isFree ? undefined : cart.step}
        headingClass={theme.heading1Class}
      >
        {cart.step === 0 ? (
          <Animation type="splash-in">
            <ReviewStep
              isFree={isFree}
              showDOB={showDOB}
              showGuardianEmail={showGuardianEmail}
            />
          </Animation>
        ) : null}
        {cart.step === 1 ? (
          <Animation type="splash-in">
            <PaymentStep
              isFree={isFree}
              partnerSiteBaseURL={partnerSiteBaseURL}
              showDOB={showDOB}
              stripePublicKey={stripePublicKey}
            />
          </Animation>
        ) : null}
      </Layout>
    );
  }

  return <div data-testid="checkout-should-not-be-here" />;
}
